/**
 * merchant-domain.ts: the one merchant-host canonicalization every offers
 * writer and reader shares (docs/OFFERS_ENGINE_V2.md section 2.4, R51, R69,
 * R71).
 *
 * Two hosts that are the same shop must produce one `merchant_domain`, or
 * `product_offers` grows one row per host variant and the panel lists the
 * same store twice: `sameday.costco.ca` is Costco, `shop.arcteryx.com` is
 * Arc'teryx, `ca.hp.com` is HP. This module reduces a host to that identity
 * in two steps: an exact-host alias table (`merchant_aliases`, migration
 * 0046, for the cases no rule can express) and a rule that strips generic
 * leading labels (`www`, `shop`, `sameday`, `en`, `ca`, ...) while a
 * registrable-looking host remains.
 *
 * Amazon hosts are never rewritten (R71): `amazon.ca` and `amazon.com` are
 * different merchants, and `smile.amazon.com` stays what it is.
 *
 * PURE and SELF-CONTAINED on purpose: no imports, no registry, no I/O. The
 * scraper gateway holds a byte-identical copy at
 * `scraper/gateway/src/identity/merchant-domain.ts` (the
 * normalize-product-name.ts precedent) checked by
 * `npm run self-test:identity`, and both copies are pinned by the shared
 * fixture `scraper/gateway/fixtures/merchant-domain.json`. A helper imported
 * from elsewhere in `shared/` would make the copy diverge, so the host
 * normalization lives here and rank-offers.ts re-exports it.
 */

/**
 * Host labels that never identify a merchant on their own: storefront,
 * device, checkout and language/country prefixes. Stripped from the left
 * while a registrable host remains.
 */
export const GENERIC_HOST_LABELS: ReadonlySet<string> = new Set([
  "www", "www2", "m", "mobile", "shop", "store", "secure", "checkout", "order",
  "sameday", "app", "en", "fr", "ca", "us", "uk", "gb", "eu", "intl",
]);

/**
 * Host identity: lowercase, no scheme, no path, no trailing dot, no leading
 * "www.". This is the normalization `product_offers.merchant_domain` is
 * written with and the first step of `canonicalMerchantDomain`.
 */
export function normalizeMerchantDomain(domain: string): string {
  return domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "")
    .replace(/^www\./, "");
}

/** Public-suffix-like second labels under which a host needs three labels to
 *  be registrable (`currys.co.uk`, `example.com.au`). */
const SECOND_LEVEL_LABELS = /^(co|com|org|net|ac|gov)$/;

/**
 * The merchant identity of a host.
 *
 * 1. Normalize (lowercase, no scheme, no path, no "www.").
 * 2. An exact alias wins outright (`sameday.costco.ca` -> `costco.ca`).
 * 3. Otherwise strip generic leading labels one at a time while at least a
 *    registrable-looking host remains: two labels, or three when the second
 *    is `co` / `com` / `org` / `net` / `ac` / `gov`. An Amazon host is never
 *    rewritten, whatever label sits in front of it.
 * 4. The stripped host is looked up in the aliases once more.
 *
 * `aliases` is the `merchant_aliases` table as an exact-host map
 * (`alias_host -> merchant_domain`); pass an empty Map when the table is not
 * loaded and the label rule still applies.
 */
export function canonicalMerchantDomain(
  host: string,
  aliases: ReadonlyMap<string, string>,
): string {
  let h = normalizeMerchantDomain(host);
  const alias = aliases.get(h);
  if (alias) return alias;
  for (;;) {
    const labels = h.split(".");
    const minLabels = SECOND_LEVEL_LABELS.test(labels[labels.length - 2] ?? "") ? 3 : 2;
    if (labels.length <= minLabels) break;
    if (!GENERIC_HOST_LABELS.has(labels[0]!)) break;
    if (/^amazon\./.test(labels.slice(1).join("."))) break;
    h = labels.slice(1).join(".");
  }
  return aliases.get(h) ?? h;
}
